import React, {
  KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  cn,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rewardops/ui'
import { Select } from '@rewardops/ui/src'

import { styling } from '~/components/styling'

type IMapleSelect = {
  className?: string
  selectItemClassName?: string
  id: string
  placeholder: string | JSX.Element
  defaultValue?: string | undefined
  options: { key: string; value: string; disabled?: boolean }[]
  onValueChange: (s: string) => void
  dontShowChevron?: boolean
  withBorder?: boolean
  fixedPlaceholder?: string | JSX.Element
  maxHeight?: string
  disabled?: boolean
  selectedValue?: string | undefined
}

export const MapleSelect = ({
  className,
  selectItemClassName,
  id,
  placeholder,
  defaultValue,
  options,
  onValueChange,
  dontShowChevron,
  fixedPlaceholder,
  withBorder = false,
  maxHeight = 'max-h-64',
  disabled = false,
  selectedValue,
}: IMapleSelect) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const conditionalProps = selectedValue ? { value: selectedValue } : undefined

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setIsKeyboardNavigation(true)
      }
    }

    const handleMouseDown = () => {
      setIsKeyboardNavigation(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return

    switch (e.key) {
      case ' ':
      case 'Enter':
      case 'ArrowDown':
        e.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'Escape':
        if (isOpen) {
          e.preventDefault()
          setIsOpen(false)
        }
        break
    }
  }

  return (
    <div className="relative">
      <Select
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
        open={isOpen}
        onOpenChange={setIsOpen}
        {...conditionalProps}
      >
        <SelectTrigger
          ref={triggerRef}
          id={id}
          variant="maple"
          dontShowChevron={dontShowChevron}
          fixedPlaceholder={fixedPlaceholder as any}
          className={cn(
            'min-h-10 flex w-full items-center justify-between gap-1 text-right text-base font-semibold',
            isKeyboardNavigation &&
              'focus:rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            !withBorder && 'border-none',
            className,
          )}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            selectedValue ? `${id}-${selectedValue}` : undefined
          }
        >
          <div className="truncate">
            <SelectValue placeholder={placeholder} />
          </div>
        </SelectTrigger>
        <SelectContent
          ref={contentRef}
          id={`${id}-listbox`}
          className="overflow-hidden rounded-md border bg-white shadow-md"
          position="popper"
          sideOffset={4}
        >
          <SelectGroup
            className={cn(
              'overflow-y-auto',
              maxHeight,
              'scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-gray-500',
            )}
            role="listbox"
            aria-label={
              typeof placeholder === 'string' ? placeholder : 'Select options'
            }
          >
            {options.map((option) => (
              <SelectItem
                className="justify-start px-4"
                key={option.key}
                value={option.key}
                id={`${id}-${option.key}`}
                disabled={
                  option.hasOwnProperty('disabled') ? option.disabled : false
                }
                role="option"
                aria-selected={selectedValue === option.key}
              >
                <div
                  className={cn(
                    'inline-block text-sm font-semibold',
                    selectItemClassName,
                    option.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {option.value}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}
